'use client'

import { trackStructuredEvent } from '@/services/snowplow'

/**
 * Fire the Snowplow `user_accounts` login event at ui-react parity
 * (`AccountServiceEvents.createLogInEvent`): `label` = provider, `property` carries
 * `auto` (silent vs explicit), `method` ('button' | 'one-tap'), `interaction`
 * (One-Tap `select_by`), `email`, `success`. No-op when the tracker is absent.
 */
export function trackLoginEvent(opts: {
    provider: string
    method?: string
    interaction?: string
    email?: string | null
    success: boolean
    auto: boolean
}): void {
    trackStructuredEvent({
        category: 'user_accounts',
        action: 'login',
        label: opts.provider,
        property: {
            auto: opts.auto,
            method: opts.method,
            interaction: opts.interaction,
            email: opts.email ?? null,
            success: opts.success,
        },
    })
}

/**
 * Fire the Snowplow `navigation` / `click` structured event at ui-react parity
 * (`AccountEvents.clicked`): the nav-bar Log In / Log Out *clicks*. `label` is the
 * clicked control (`log_in` | `log_out`); `property.nav_type` distinguishes the
 * desktop dropdown (`nav_bar`) from the mobile hamburger menu (`hamburger`).
 *
 * This is a distinct event family from the `user_accounts` login event
 * (`trackLoginEvent`) and the logout identity-clear: ui-react fires this on the
 * button click in addition to — not instead of — the login lifecycle, and it is
 * the *only* Snowplow signal a logout click produces. No-op when the tracker is absent.
 */
export function trackNavClickEvent(opts: { label: string; navType: string }): void {
    trackStructuredEvent({
        category: 'navigation',
        action: 'click',
        label: opts.label,
        property: { navType: opts.navType },
    })
}
